import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  getCustomer,
  getCustomerComplaints,
  getCustomerRefunds,
  getCustomers,
  sendCustomerPasswordReset,
  setCustomerStatus,
} from './customers.api';
import type { CustomerListParams } from '../types/customer.types';

export const customerKeys = {
  all: ['customers'] as const,
  list: (params: CustomerListParams) => [...customerKeys.all, 'list', params] as const,
  detail: (customerId: number) => [...customerKeys.all, 'detail', customerId] as const,
  complaints: (customerId: number) => [...customerKeys.detail(customerId), 'complaints'] as const,
  refunds: (customerId: number) => [...customerKeys.detail(customerId), 'refunds'] as const,
};

export function useCustomers(params: CustomerListParams) {
  return useQuery({
    queryKey: customerKeys.list(params),
    queryFn: ({ signal }) => getCustomers(params, signal),
    placeholderData: (previous) => previous,
  });
}

export function useCustomer(customerId: number | undefined) {
  return useQuery({
    queryKey: customerKeys.detail(customerId ?? 0),
    queryFn: ({ signal }) => getCustomer(customerId as number, signal),
    enabled: customerId !== undefined,
  });
}

export function useCustomerComplaints(customerId: number) {
  return useQuery({
    queryKey: customerKeys.complaints(customerId),
    queryFn: ({ signal }) => getCustomerComplaints(customerId, signal),
  });
}

export function useCustomerRefunds(customerId: number) {
  return useQuery({
    queryKey: customerKeys.refunds(customerId),
    queryFn: ({ signal }) => getCustomerRefunds(customerId, signal),
  });
}

/** The account changes for everyone viewing it: the whole customers cache is refreshed. */
export function useSetCustomerStatus(customerId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { isActive: boolean; reason: string }) =>
      setCustomerStatus(customerId, input.isActive, input.reason),
    onSuccess: (_result, input) => {
      toast.success(input.isActive ? 'Account reactivated.' : 'Account deactivated.');
      void queryClient.invalidateQueries({ queryKey: customerKeys.all });
    },
  });
}

export function useSendCustomerPasswordReset(customerId: number) {
  return useMutation({
    mutationFn: (reason: string) => sendCustomerPasswordReset(customerId, reason),
    onSuccess: (sentTo) => toast.success(`Reset code sent to ${sentTo}.`),
  });
}
